<template>
  <v-container fluid>
    <PageHeaderSlot>
      <template slot="top-right-slot">
        <FormButton v-if="canEdit" text="createHoliday" isAdd class="ml-2" @click="handleAddHoliday()" />
      </template>
    </PageHeaderSlot>

    <Datatable
      :isLoading="tableLoading"
      :tableHeaders="tableHeaders"
      :tableData="tableData"
      :page.sync="tableFilter.page"
      :pageLimit.sync="tableFilter.limit"
      :itemTotal="tableItemTotal"
      @options-update="getHolidays()"
      @delete-clicked="handleDeleteHoliday($event)"
    />

    <!-- Modal: 新增假期 -->
    <FormDialog ref="addHolidayDialog" formRef="addHolidayForm" title="createHoliday">
      <v-row class="ma-0">
        <v-col cols="12" sm="6">
          <FormSelect
            label="addLessonType"
            :fieldValue.sync="addHoliday.type"
            required
            :options="addTypeOptions"
            @changed="handleAddTypeChanged()"
          />
        </v-col>

        <v-col cols="12" sm="6" v-if="addHoliday.type === 'import'">
          <FormSelect
            label="year"
            :fieldValue.sync="addHoliday.year"
            required
            :options="yearOptions"
            @changed="handleAddTypeChanged()"
          />
        </v-col>

        <v-row class="ma-0 pa-0 w-full" v-else-if="addHoliday.type === 'custom'">
          <v-col cols="12" sm="6">
            <FormDatePicker label="date" :dateValue.sync="addHoliday.date" disabledActionButtons />
          </v-col>

          <v-col cols="12" sm="6" class="d-flex">
            <FormInput label="holidayName" :fieldValue.sync="addHoliday.name" style="flex: 1;" />
            <FormButton text="add" isAdd class="ml-2 mt-7" @click="handleAddCustomHoliday()" />
          </v-col>
        </v-row>

        <v-col cols="12" sm="6">
          <FormSelect label="campus" :fieldValue.sync="addHoliday.school_id" required :options="tutorSchoolOptions" />
        </v-col>
      </v-row>

      <Datatable
        v-if="$validate.DataValid(addHoliday.type)"
        tableName="selectedHoliday"
        :tableHeaders="addTableHeaders"
        :tableData="addHoliday.list"
        disablePagination
        @delete-clicked="handleDeleteDate($event)"
        @edit-clicked="handleEditHoliday($event)"
      />
    </FormDialog>
    <!-- Modal: Edit假期 -->
    <FormDialog ref="editHolidayDialog" formRef="editHolidayForm" title="editHoliday">
      <v-row class="ma-0">
        <v-col cols="12" sm="6">
          <FormDatePicker label="date" :dateValue.sync="editHoliday.date" disabledActionButtons required readonly />
        </v-col>

        <v-col cols="12" sm="6">
          <FormInput label="holidayName" :fieldValue.sync="editHoliday.name" />
        </v-col>
      </v-row>
    </FormDialog>
  </v-container>
</template>

<script>
import FormSelect from '@/components/formField/FormSelect.vue'
import Datatable from '@/components/Datatable.vue'
import FormDialog from '@/components/FormDialog.vue'
import publicHolidays from '@/static/publicHolidays.json'
import FormDatePicker from '@/components/formField/FormDatePicker.vue'
import FormInput from '@/components/formField/FormInput.vue'

export default {
  name: 'HolidaySetting',
  inject: ['getConfirmDialogRef'],
  components: {
    FormSelect,
    Datatable,
    FormDialog,
    FormDatePicker,
    FormInput,
  },
  computed: {
    confirmRef() {
      return this.getConfirmDialogRef()
    },
    tableHeaders() {
      const vm = this
      const headers = [
        { value: 'date', text: vm.$t('date'), width: 150 },
        { value: 'name', text: vm.$t('holidayName') },
        { value: 'remark', text: vm.$t('remark') },
      ]
      if (this.canEdit) {
        headers.push({ value: 'actionDelete', text: '', align: 'end' })
      }
      return headers
    },
    canEdit() {
      // [T46] admin account 只可read
      return !this.isAppMode(['T46']) || this.userType !== 'admin'
    },
  },
  data: vm => ({
    userType: vm.gfGetUserType(),
    tableLoading: false,
    tableData: [],
    tableItemTotal: 0,

    tableFilter: {
      page: 1,
      limit: 10,
    },

    addHoliday: {
      type: '', // 'import' | 'custom',
      year: null,
      date: '',
      list: [],
      school_id: -1,
      name: '',
    },
    editHoliday: {
      date: '',
      name: '',
    },
    count: 0,

    addTableHeaders: [
      { value: 'date', text: vm.$t('date') },
      { value: 'name', text: vm.$t('holidayName') },
      { value: 'minActionEditAndDelete', text: '', align: 'end' },
    ],
    addTypeOptions: [
      { value: 'import', text: vm.$t('importPublicHoliday') },
      { value: 'custom', text: vm.$t('addSpecificDate') },
    ],
    publicHolidayList: [],
    yearOptions: [],
    tutorSchoolOptions: [],
  }),
  methods: {
    async getHolidays(regenUrl = true) {
      if (regenUrl === true) {
        this._regenerateURL()
      }
      this.tableLoading = true
      try {
        let payload = {
          filter_limit: this.tableFilter.limit,
          filter_page: this.tableFilter.page - 1,
        }

        const selectedSchool = this.gfGetUserSelectedSchool()
        if (selectedSchool > -1) {
          payload['center_id'] = selectedSchool
        }

        const { data, total } = await this.$Fetcher.GetHolidays(payload)
        this.tableItemTotal = total
        this.tableData = data.map(item => {
          this.count += 1
          return {
            ...item,
            remark: '',
            raw_id: item.id,
            id: this.count,
          }
        })
        this.getHolidayLesson()
      } catch (err) {
        this.$common.error(err)
        this.tableLoading = false
        this.tableData = []
        if (this.tableFilter.page === 1) {
          this.tableItemTotal = 0
        }
      }
    },
    async getHolidayLesson() {
      if (this.tableData.length) {
        try {
          let payload = {
            filter_limit: this.tableFilter.limit,
            filter_page: this.tableFilter.page - 1,
            check_lesson: '',
            check_date_list: this.tableData.map(el => el.date),
          }
          const { data } = await this.$Fetcher.GetHolidays(payload)
          let centerIds = []
          for (let i = 0; i < this.tableData.length; i++) {
            const found = data.find(el => {
              if (this.$validate.DataValid(this.tableData[i].raw_id)) {
                return el.id === this.tableData[i].raw_id
              } else {
                return el.date === this.tableData[i].date && el.center_id === this.tableData[i].center_id
              }
            })
            if (found) {
              if (this.$validate.DataValid(found.lessons) && found.lessons.length > 0) {
                this.tableData[i]['remark'] = this.$t('haveLesson')
                this.tableData[i]['lessons'] = found.lessons

                const lessonCenters = found.lessons.map(el => el.center_id)
                centerIds = [...centerIds, ...lessonCenters]
              }
            }
          }

          await this.getLessonCenter(centerIds)
        } catch {}
      }

      this.tableLoading = false
    },
    async getLessonCenter(ids) {
      const list = ids.filter(el => this.$validate.DataValid(el) && el > -1)
      if (list.length > 0) {
        try {
          const data = await this.$Fetcher.GetSchoolsByIdList(list)

          for (let i = 0; i < this.tableData.length; i++) {
            if (this.$validate.DataValid(this.tableData[i].lessons)) {
              const centerIds = []
              for (let j = 0; j < this.tableData[i].lessons.length; j++) {
                if (!centerIds.includes(this.tableData[i].lessons[j].center_id)) {
                  centerIds.push(this.tableData[i].lessons[j].center_id)
                }
              }

              for (let j = 0; j < centerIds.length; j++) {
                const found = data.find(el => el.id === centerIds[j])
                if (found) {
                  centerIds[j] = found.name
                }
              }

              this.tableData[i]['remark'] = `${this.$t('haveLesson')} (${centerIds.join(', ')})`
            }
          }
        } catch {}
      }
    },

    async getPublicHoliday() {
      try {
        this.publicHolidayList = []
        if (
          this.$validate.DataValid(publicHolidays) &&
          this.$validate.DataValid(publicHolidays.vcalendar) &&
          publicHolidays.vcalendar.length > 0
        ) {
          const list = publicHolidays.vcalendar[0].vevent
          for (let i = 0; i < list.length; i++) {
            const d = list[i].dtstart[0]
            const year = parseInt(d.substring(0, 4))
            const found = this.publicHolidayList.find(el => el.year === year)
            const date = `${year}-${d.substring(4, 6)}-${d.substring(6, 8)}`
            let obj = {
              date: date,
              summary: list[i].summary || '',
            }
            if (found) {
              found.dates.push(obj)
            } else {
              this.publicHolidayList.push({
                year: year,
                dates: [obj],
              })
            }
          }
        }
      } catch (err) {
        this.$common.error(err)
      }
    },

    // -- (刪除)
    async handleDeleteHoliday(id) {
      if (this.$store.getters.isLoading) {
        this.$store.dispatch('toggleSnack', { message: 'processing' })
        return
      }

      if (!this.canEdit) {
        this.$store.dispatch('toggleSnack', { message: 'noPermission' })
        return
      }

      const confirm = await this.confirmRef.show({ isDelete: true })
      if (confirm) {
        this.$store.dispatch('setLoading', true)

        try {
          let _id = null
          const found = this.tableData.find(el => el.id === id)
          if (found) {
            if (!this.$validate.DataValid(found.raw_id)) {
              const data = await this.$Fetcher.GetHolidayByDateAndCenter(found.date, found.center_id)
              _id = data.id
            } else {
              _id = found.raw_id
            }
          }

          if (!this.$validate.DataValid(_id)) {
            throw 'message.holidayNotFound'
          }

          await this.$Fetcher.DeleteHoliday(_id)
          this.$store.dispatch('toggleSnack', {
            message: 'message.deleteSuccess',
            type: 'success',
          })
          this.tableFilter.page = 1
          this.getHolidays()
        } catch (err) {
          this.showAlert({
            title: 'message.deleteFail',
            message: err,
            isError: true,
          })
        } finally {
          this.$store.dispatch('setLoading', false)
        }
      }
    },

    // ============= 新增假期 ==============
    handleAddTypeChanged() {
      this.addHoliday.list = []
      if (this.addHoliday.type === 'import') {
        if (this.$validate.DataValid(this.addHoliday.year)) {
          const found = this.publicHolidayList.find(el => el.year == this.addHoliday.year)
          if (found) {
            for (let i = 0; i < found.dates.length; i++) {
              this.addHoliday.list.push({
                id: i,
                date: found.dates[i].date,
                name: found.dates[i].summary,
              })
            }
          }
        }
      }
    },
    handleAddCustomHoliday() {
      if (this.$validate.DataValid(this.addHoliday.date)) {
        const found = this.addHoliday.list.find(el => el.date === this.addHoliday.date)
        if (!found) {
          this.count += 1
          this.addHoliday.list.push({
            id: this.count,
            date: this.addHoliday.date,
            name: this.addHoliday.name,
          })
          this.addHoliday.list.sort((a, b) => this.$formatter.sortDate(a.date, b.date));
          this.addHoliday.date = ''
          this.addHoliday.name = ''
        }
      }
    },
    async handleAddHoliday(newOpen = true) {
      if (newOpen) {
        if (this.$store.getters.isLoading) {
          this.$store.dispatch('toggleSnack', { message: 'processing' })
          return
        }

        if (!this.canEdit) {
          this.$store.dispatch('toggleSnack', { message: 'noPermission' })
          return
        }

        this.addHoliday.type = ''
        this.addHoliday.year = null
        this.addHoliday.date = ''
        this.addHoliday.name = ''
        this.addHoliday.list = []
        await this.$refs.addHolidayDialog.resetForm()
        this.addHoliday.school_id = -1
        const found = this.tutorSchoolOptions.find(el => el.value !== -1)
        if (found) {
          this.addHoliday.school_id = found.value
        }
      } else {
        this.$refs.addHolidayDialog.setLoading(false)
      }

      const confirm = await this.$refs.addHolidayDialog.show()
      if (confirm) {
        if (this.addHoliday.list.length < 1) {
          this.$store.dispatch('toggleSnack', {
            type: 'error',
            message: 'message.atLeastOneHoliday',
          })
          this.handleAddHoliday(false)
          return
        }

        this._submitHolidayServer()
      }
    },
    async _submitHolidayServer(skip_check = false) {
      const dates = this.addHoliday.list.map(item => {
        return {
          date: item.date,
          summary: item.name,
        }
      })
      try {
        await this.$Fetcher.NewHoliday(dates, skip_check, this.addHoliday.school_id)
        this.$store.dispatch('toggleSnack', {
          message: 'message.createSuccess',
          type: 'success',
        })
        this.tableLoading = true
        this.$refs.addHolidayDialog.hide()
        setTimeout(() => {
          this.tableFilter.page = 1
          this.getHolidays(false)
        }, 1000)
      } catch (err) {
        this.$common.error(err)

        if (err.error !== undefined && err.data !== undefined) {
          if (err.error === 'lesson_exist') {
            const confirm = await this.confirmRef.show({
              message: this.$t('message.holidayHaveLesson', { dates: err.data.join(', ') }),
            })
            if (confirm) {
              this._submitHolidayServer(true)
            } else {
              this.$refs.addHolidayDialog.hide()
            }

            return
          } else if (err.error === 'date_exist') {
            this.showAlert({
              title: 'message.createFail',
              message: `${this.$t('message.holidayExist')} (${err.data.join(', ')})`,
              isError: true,
            })
          } else {
            this.showAlert({
              title: 'message.createFail',
              message: JSON.stringify(err),
              isError: true,
            })
          }
        } else {
          this.showAlert({
            title: 'message.createFail',
            message: err,
            isError: true,
          })
        }

        this.handleAddHoliday(false)
      }
    },
    handleDeleteDate(id) {
      const findPos = this.addHoliday.list.findIndex(el => el.id === id)
      this.addHoliday.list.splice(findPos, 1)
    },

    async handleEditHoliday(id, newOpen = true) {
      if (newOpen) {
        if (this.$store.getters.isLoading) {
          this.$store.dispatch('toggleSnack', { message: 'processing' })
          return
        }

        this.editHoliday.date = ''
        this.editHoliday.name = ''
        await this.$refs.editHolidayDialog.resetForm()
        const found = this.addHoliday.list.find(el => el.id === id)
        if (found) {
          this.editHoliday.date = found.date
          this.editHoliday.name = found.name
        } else {
          return
        }
      } else {
        this.$refs.editHolidayDialog.setLoading(false)
      }
      const confirm = await this.$refs.editHolidayDialog.show()
      if (confirm) {
        for (let i = 0; i < this.addHoliday.list.length; i++) {
          if (this.addHoliday.list[i].id === id) {
            this.addHoliday.list[i].name = this.editHoliday.name
            break
          }
        }
        this.$refs.editHolidayDialog.hide()
      }
    },

    // ======== 分校 Options =======
    async getSchoolOptions() {
      try {
        this.tutorSchoolOptions = [{ value: -1, text: this.$t('allCenters') }]
        const selectedSchool = this.gfGetUserSelectedSchool()
        if (selectedSchool > -1) {
          const data = await this.$Fetcher.GetSchoolById(selectedSchool)
          this.tutorSchoolOptions.push({ value: data.id, text: data.name })
        }
      } catch (err) {
        this.$common.error(err)
      }
    },

    _getQuery() {
      this.tableFilter.page = 1
      this.tableFilter.limit = 10
      const query = this.gfGetTableQuery({
        page: 'int',
        limit: 'int',
      })
      for (const key in this.tableFilter) {
        if (this.$validate.DataValid(query[key])) {
          this.$set(this.tableFilter, key, query[key])
        }
      }
    },
    _regenerateURL() {
      let q = {
        page: this.tableFilter.limit !== 10 || this.tableFilter.page !== 1 ? this.tableFilter.page : null,
        limit: this.tableFilter.limit !== 10 || this.tableFilter.page !== 1 ? this.tableFilter.limit : null,
      }
      const newQuery = this.$common.diffQuery(q, this.$route.query)
      if (newQuery) this.$router.replace({ query: newQuery })
    },

    async handleSiteLoaded() {
      this.$common.log('>>>>>>>>>>>>>>>>>>>>>>>>>>>> handleSiteLoaded: HolidaySetting')
      this.userType = this.gfGetUserType()
      this.tableLoading = true
      this._getQuery()
      await this.getPublicHoliday()
      await this.getSchoolOptions()
      this.$store.dispatch('toggleLoadingPage', false)
      this.getHolidays(false)
    },
  },

  destroyed() {
    window.removeEventListener('onSiteLoaded', this.handleSiteLoaded)
  },
  mounted() {
    this.$store.dispatch('toggleLoadingPage', true)
    window.addEventListener('onSiteLoaded', this.handleSiteLoaded)
  },
  created() {
    // get this year & next year
    const today = this.$formatter.convertDate()
    let thisYear = today.getFullYear()
    this.yearOptions = [{ value: thisYear, text: thisYear }]
    thisYear += 1
    this.yearOptions.push({ value: thisYear, text: thisYear })
  },

  // ------ navigation guard ------
  beforeRouteLeave(to, from, next) {
    if (this.$store.getters.isLoading) {
      this.$store.dispatch('toggleSnack', { message: 'processing' })
      next(false)
    } else if (this.$store.getters.dataIsUpdated) {
      this.$store.dispatch('showLeaveAlert', { toLink: { name: to.name } })
      next(false)
    } else {
      this.$XHR.abort()
      next()
    }
  },
}
</script>
